<script setup lang="ts">
    import { onMounted, onBeforeUnmount, ref, watch, render, h } from 'vue';
    import 'gridstack/dist/gridstack.min.css';

    import { WidgetLayout } from '../../services/api/dashboard/types';
    import { IWidget, MergedWidget } from '../../services/api/widget/types';
    import { useGrid, createMergedWidgets } from '@/js/stores/grid';
    import GridWidget from '../widgets/GridWidget.vue';
    import { GridStack, GridStackWidget } from 'gridstack';
    import { gridDefaultOptions } from './const';
    import PlaceholderWidget from '../widgets/PlaceholderWidget.vue';

    const props = withDefaults(
        defineProps<{
            widgetData: Array<IWidget>;
            widgetLayout: WidgetLayout[];
            dashboardId: string;
            staticGrid: boolean;
            container?: 'default' | 'modal';
        }>(),
        {
            container: 'default',
        }
    );

    const emit = defineEmits<{
        (e: 'editWidget', widgetId: string): void;
        (e: 'deleteWidget', widgetId: string, name: string): void;
        (e: 'updateComment', widgetId: string, value: string): void;
    }>();

    const localGrid = ref<GridStack | null>(null);

    const gridStore = useGrid();

    onMounted(() => {
        if (props.staticGrid) {
            initStaticGrid();
        } else {
            initInteractiveGrid();
        }
    });

    onBeforeUnmount(() => {
        if (props.staticGrid) {
            localGrid.value?.destroy();
            localGrid.value = null;
        } else {
            gridStore.destroyGrid();
        }
    });

    function initStaticGrid() {
        GridStack.renderCB = renderWidget;
        localGrid.value = GridStack.init(
            { ...gridDefaultOptions, staticGrid: true },
            `[data-grid-container='${props.container}']`
        );

        const merged = createMergedWidgets(props.widgetLayout, props.widgetData);
        localGrid.value.load(merged, true);

        watch(
            [() => props.widgetData],
            () => {
                const updated = createMergedWidgets(props.widgetLayout, props.widgetData);
                localGrid.value?.load(updated, true);
            },
            { deep: true }
        );
    }

    function initInteractiveGrid() {
        GridStack.renderCB = renderWidget;
        gridStore.grid = GridStack.init(
            { ...gridDefaultOptions, staticGrid: false },
            `[data-grid-container='${props.container}']`
        );
        gridStore.grid.on('change', () => gridStore.updateDashboardLayout(props.dashboardId));

        gridStore.mergeWidgets(props.widgetLayout, props.widgetData);
        gridStore.loadGridItems(false);

        watch(
            [() => props.widgetData],
            () => {
                gridStore.mergeWidgets(props.widgetLayout, props.widgetData);
                gridStore.loadGridItems(false);
            },
            { immediate: true, deep: true }
        );
    }

    function renderWidget(el: HTMLElement, widget: GridStackWidget) {
        let chart;
        if (widget.id === 'Empty' && !props.staticGrid) {
            chart = h(PlaceholderWidget, {
                onOpenForm: () => emit('editWidget', 'new'),
            });
        } else {
            const widgetWithChart = widget as MergedWidget;
            chart = h(GridWidget, {
                widget: widgetWithChart,
                dashboardId: props.dashboardId,
                isStatic: props.staticGrid,
                ...(!props.staticGrid && {
                    onDeleteWidget: () =>
                        emit('deleteWidget', widgetWithChart.id, widgetWithChart.title),
                    onEditWidget: () => emit('editWidget', widgetWithChart.id),
                    onUpdateComment: (value: string) =>
                        emit('updateComment', widgetWithChart.id, value),
                }),
            });
        }
        render(chart, el);
    }
</script>

<template>
    <div class="Grid">
        <div class="grid-stack" :data-grid-container="props.container" />
    </div>
</template>
